/**
 * ContextManagerFactory is the singleton entry point to the methods of ContextManager.
 * The Context Manager allows users to register context sensitive elements of the UI and manages when they should be
 * applied in the UI. Currently, the Context Manager is capable of managing the Tab Groups and Quick Access actions
 * associated with a context.
 *
 * @example <caption>Example usage of ContextManager in MATLAB Online</caption>
 *
 * // Build Toolstrip
 * uiBuilder.create({
 *     tag: "motwToolstrip",
 *     type: TypeConstants.TOOLSTRIP
 * });
 *
 * // Get ContextManager singleton
 * var contextManager = ContextManager.getInstance({
 *     parentAppId: "desktop",
 *     toolstripTag: "motwToolstrip",
 *     qabContextualControlsActionMap: QABContextualControlsActionMap
 * });
 *
 * // Build Parent's Global Tab Group
 * application.uiBuilder.addTabGroupFromConfig(GlobalTabGroupConfig, "motwToolstrip.globalTabGroup");
 *
 * // Register Parent's Global Tab Group
 * application.contextManager.register("TabGroups", {
 *     appId: "desktop",
 *     contextId: "global",
 *     tabGroupTags: ["motwToolstrip.globalTabGroup"]
 * });
 *
 * // Build Editor Tab Group
 * uiBuilder.addTabGroupFromConfig(EditorTabGroupConfig, "motwToolstrip.editorTabGroup");
 *
 * // Register Editor Tab Group
 * application.contextManager.register("TabGroups", {
 *     appId: "editorFile",
 *     contextId: "global",
 *     tabGroupTags: ["motwToolstrip.editorTabGroup"]
 * });
 *
 * // Register Contextual Action for Edtior's Redo
 * application.contextManager.register("QuickAccessAction", {
 *     appId: "editorFile",
 *     contextId: "global",
 *     quickAccessControlTag: QABContextualControls.REDO,
 *     actionId: ActionIdConstants.REDO_EDITOR
 * });
 *
 * // Register Contextual Action for Editor's Undo
 * application.contextManager.register("QuickAccessAction", {
 *     appId: "editorFile",
 *     contextId: "global",
 *     quickAccessControlTag: QABContextualControls.UNDO,
 *     actionId: ActionIdConstants.UNDO_EDITOR
 * });
 *
 * // Register Contextual Action for Command Window's Redo
 * application.contextManager.register("QuickAccessAction", {
 *     appId: "commandWindow",
 *     contextId: "global",
 *     quickAccessControlTag: QABContextualControls.REDO,
 *     actionId: ActionIdConstants.REDO_CMD
 * });
 *
 * // Register Contextual Action for Command Window's Redo
 * application.contextManager.register("QuickAccessAction", {
 *     appId: "commandWindow",
 *     contextId: "global",
 *     quickAccessControlTag: QABContextualControls.UNDO,
 *     actionId: ActionIdConstants.UNDO_CMD
 * });
 *
 * // Build Figure Tab Group
 * application.uiBuilder.addTabGroupFromConfig(FigureTabGroupConfig, "motwToolstrip.figureTabGroup");
 *
 * // Register Figure Tab Group
 * application.contextManager.register("TabGroups", {
 *     appId: "figure",
 *     contextId: "global",
 *     tabGroupTags: ["motwToolstrip.figureTabGroup"]
 * });
 *
 * // The context can be driven by listening to focus changes in the application or through any other means
 * // The following will cause the Tab Groups registered for the editor's global context to be added to the Toolstrip as
 * // well as the Undo and Redo buttons in the QAB to be rewired to work for the editor
 * contextManager.set("currentContext", {appId: "editorFile", contextId: "global"});
 *
 * @module MW/uiframework/contextmanager/ContextManagerFactory
 *
 * @copyright Copyright 2013-14 The MathWorks, Inc.
 */

define([
    "./ContextManager"
], function (ContextManager) {
    var _instance = null;

    return {
        /**
         * Returns the singleton instance of ContextManager, creates one if it doesn't exist yet
         * @param {Object} args - This must contain the "parentAppId", "toolstripTag" and the
         * "qabContextualControlsActionMap"
         * @param {String} args.parentAppId - An id that will correspond to the parentApp. For example, in the case of
         * MATLAB Online, this could be something like "desktop" or for App Designer it could be "App Designer".
         * @param {String} args.toolstripTag - The tag of a pre-built toolstrip that will be controlled by this Context
         * Manager
         * @param {Object} args.qabContextualControlsActionMap - A map of contextual quick access controls and their
         * corresponding default placeholder action ids
         * @returns An instance of MW/uiframework/contextmanager/ContextManager
         * @instance
         */
        getInstance: function (args) {
            if (!_instance) {
                _instance = new ContextManager(args);
            }
            return _instance;
        },

        /**
         * This cleans up the singleton Context Manager instance
         * @instance
         */
        destroy: function () {
            if (_instance) {
                _instance.cleanup();
                _instance = null;
            }
        }
    };
});